import React from 'react';
import Select from '../../engine/SelectEngine';
import useTranslations from '../../../hooks/useTranslations';
import Animation from '../../Animation';
import styled from 'styled-components/macro';
import TimeBasedSchedulerGeneratorEngine from '../../engine/TimeBasedSchedulerGeneratorEngine';
import StatusBasedSchedulerGeneratorEngine from '../../engine/StatusBasedSchedulerGeneratorEngine';
import DateFieldSchedulerGeneratorEngine from '../../engine/DateFieldSchedulerGeneratorEngine';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const AutomationsTriggerView = props => {
  const t = useTranslations();
  return (
    <Container>
      <Select
        value={props.trigger}
        onChange={props.onTriggerChange}
        fullWidth
        placeholder={t('automations.wizard.trigger.select.placeholder')}
        options={[
          {
            isSectionBreak: true,
            text: t('automaions.wizard.trigger.action-based')
          },
          {
            value: 'created',
            text: t('automaions.wizard.trigger.created'),
            'data-testid': 'created'
          },
          {
            value: 'submitted',
            text: t('automaions.wizard.trigger.submitted'),
            'data-testid': 'submitted'
          },
          {
            value: 'first_update',
            text: t('automaions.wizard.trigger.first_update'),
            'data-testid': 'first_update'
          },
          {
            value: 'request_more_info',
            text: t('automaions.wizard.trigger.request_more_info'),
            'data-testid': 'request_more_info'
          },
          {
            value: 'admin_save_changes',
            text: t('automaions.wizard.trigger.admin_save_changes'),
            'data-testid': 'admin_save_changes'
          },
          ...(props.hasWorldcheck
            ? [
                {
                  value: 'worldcheck_status_change',
                  text: t('automaions.wizard.trigger.worldcheck_status_change'),
                  'data-testid': 'worldcheck_ongoing'
                }
              ]
            : []),
          ...(props.hasCredas
            ? [
                {
                  value: 'credas_status_change',
                  text: t('automaions.wizard.trigger.credas_status_change'),
                  'data-testid': 'credas_status_change'
                },
                {
                  value: 'credas_name_check_update',
                  text: t('automaions.wizard.trigger.credas_name_check_update'),
                  'data-testid': 'credas_name_check_update'
                },
                {
                  value: 'credas_liveness',
                  text: t('automaions.wizard.trigger.credas_liveness'),
                  'data-testid': 'credas_liveness'
                },
                {
                  value: 'credas_id_ducument_update',
                  text: t(
                    'automaions.wizard.trigger.credas_id_ducument_update'
                  ),
                  'data-testid': 'credas_id_ducument_update'
                },
                {
                  value: 'credas_non_biometric_document',
                  text: t(
                    'automaions.wizard.trigger.credas_non_biometric_document'
                  ),
                  'data-testid': 'credas_non_biometric_document'
                },
                {
                  value: 'credas_data_check_update',
                  text: t('automaions.wizard.trigger.credas_data_check_update'),
                  'data-testid': 'credas_data_check_update'
                }
              ]
            : []),
          ...(props.hasThirdfort
            ? [
                {
                  value: 'thirdfort_status_change',
                  text: t('automaions.wizard.trigger.thirdfort_status_change'),
                  'data-testid': 'thirdfort_status_change'
                },
                {
                  value: 'thirdfort_report_ready',
                  text: t('automaions.wizard.trigger.thirdfort_report_ready'),
                  'data-testid': 'thirdfort_report_ready'
                },
                {
                  value: 'thirdfort_ongoing_update',
                  text: t('automaions.wizard.trigger.thirdfort_ongoing_update'),
                  'data-testid': 'thirdfort_ongoing_update'
                },
                {
                  value: 'thirdfort_ongoing_reminder',
                  text: t(
                    'automaions.wizard.trigger.thirdfort_ongoing_reminder'
                  ),
                  'data-testid': 'thirdfort_ongoing_reminder'
                },
                {
                  value: 'thirdfort_transaction_change_ongoing_off',
                  text: t(
                    'automaions.wizard.trigger.thirdfort_transaction_change_ongoing_off'
                  ),
                  'data-testid': 'thirdfort_transaction_change_ongoing_off'
                },
                {
                  value: 'thirdfort_additional_document_upload',
                  text: t(
                    'automaions.wizard.trigger.thirdfort_additional_document_upload'
                  ),
                  'data-testid': 'thirdfort_additional_document_upload'
                }
              ]
            : []),
          {
            isSectionBreak: true,
            text: t('automaions.wizard.trigger.time-based')
          },
          {
            value: 'time_since_creation',
            text: t('automaions.wizard.trigger.time_since_creation'),
            'data-testid': 'time_since_creation'
          },
          {
            value: 'time_since_last_update',
            text: t('automaions.wizard.trigger.time_since_last_update'),
            'data-testid': 'time_since_last_update'
          },
          {
            value: 'time_since_last_status_change',
            text: t('automaions.wizard.trigger.time_since_last_status_change'),
            'data-testid': 'time_since_last_status_change'
          },
          {
            value: 'date_field',
            text: t('automaions.wizard.trigger.date_field'),
            'data-testid': 'date_field',
            disabled: !props.dateFields.length
          },
          ...(!props.customButtons.length
            ? []
            : [
                {
                  isSectionBreak: true,
                  text: t('automaions.wizard.trigger.custom-buttons')
                },
                ...props.customButtons.map(cb => ({
                  value: cb.id,
                  text: t(cb.label),
                  'data-testid': `cb-${cb.label}-${cb.id}`
                }))
              ])
        ]}
        customCSS="max-width:30rem;"
      />
      <Animation
        type="collapse"
        in={props.showDateFieldBased}
        mountOnEnter
        unmountOnExit
      >
        <DateFieldSchedulerGeneratorEngine
          update={props.onTriggerSchedulerChange}
          schedule={props.schedulerData}
          dateFields={props.dateFields}
        />
      </Animation>
      <Animation
        type="collapse"
        in={props.showDateBased}
        mountOnEnter
        unmountOnExit
      >
        <TimeBasedSchedulerGeneratorEngine
          update={props.onTriggerSchedulerChange}
          schedule={props.schedulerData}
        />
      </Animation>
      <Animation
        type="collapse"
        in={props.showStatusBased}
        mountOnEnter
        unmountOnExit
      >
        <StatusBasedSchedulerGeneratorEngine
          update={props.onTriggerSchedulerChange}
          schedule={props.schedulerData}
        />
      </Animation>
    </Container>
  );
};

export default AutomationsTriggerView;
